import { SessionChoicePage } from '@/components/session/choice-page';
import { SessionDeadLink } from '@/components/session/dead-link';
import { SessionResumePage } from '@/components/session/resume-page';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { ApiError } from '@/lib/api';
import { fetchLinkOffer, fetchSession, startSession } from '@/lib/session-api';
import { isDeadEntryRefusal, sessionRefusalSentence } from '@/lib/session-labels';
import { forgetToken, rememberToken, rememberedToken } from '@/lib/session-memory';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

/**
 * `/l/<secret>` - the DOOR (§5.1, §5.3), in the public shell and at the PMS
 * colours.
 *
 * « `/l/...` reste dans la coquille publique, aux couleurs PMS : c'est encore
 * une porte d'accueil, et elle ne doit rien promettre du visuel de
 * l'expérience. » Nothing of a theme is loaded here; the experience paints
 * itself only at `/s/<jeton>`.
 *
 * ## The order of §5.3, and why nothing is drawn before the second answer
 *
 * The browser remembers the LAST session it started through this secret - a
 * token in `localStorage`, keyed by the secret, never a cookie shared between
 * links - and « demande son STATUT au serveur avant de dessiner quoi que ce
 * soit ». So there are two reads before any decision: the offer of the link,
 * and the status of the remembered session. Drawing the choice page while the
 * second is in flight would flash a page the person is about to be taken off.
 *
 * Then, and only then:
 *
 * | what the browser remembers | what is drawn |
 * |---|---|
 * | a session, whatever its status | the reprise page of §5.3 |
 * | nothing, and the link has ONE entry | nothing: the session starts at once |
 * | nothing, and the link has several | the choice page of §5.1 |
 * | - and the link is dead | the dead screen, in all three cases |
 *
 * A remembered token the server no longer knows (a store reset, a session put
 * in the trash) is FORGOTTEN rather than shown: leaving it would make this page
 * ask about a session that will never come back, on every single opening.
 */
export const Route = createFileRoute('/l/$secret')({
  component: LinkDoorPage,
});

function LinkDoorPage() {
  const { secret } = Route.useParams();
  const navigate = useNavigate();

  /* Read ONCE, at the mount, and never watched: another tab starting a session
     through the same link does not move this page under the person reading it. */
  const [remembered] = useState(() => rememberedToken(secret));

  const offer = useQuery({
    queryKey: ['link-offer', secret],
    queryFn: () => fetchLinkOffer(secret),
    retry: false,
  });

  const previous = useQuery({
    queryKey: ['session', remembered],
    queryFn: () => fetchSession(remembered ?? ''),
    enabled: remembered !== null,
    retry: false,
    // The status is the point of this read, and a cached one would be the
    // status this browser last saw rather than the one the session has.
    refetchOnMount: 'always',
  });

  const start = useMutation({
    mutationFn: (experienceId: string | null) => startSession(secret, experienceId),
    onSuccess: (token) => {
      rememberToken(secret, token);
      void navigate({ to: '/s/$token', params: { token } });
    },
  });

  /*
    « Entrée unique sans session : démarrage direct, sans écran » (§5.3).

    An effect, and it is a WRITE rather than a fetch - the rule of this
    repository is about loading data with `useEffect`, and this is the gesture
    the person made by opening the link. The ref is not decoration: StrictMode
    plays every effect twice in development, and a second start would put a
    second session in the base for one opening.
  */
  const started = useRef(false);
  const entries = offer.data?.state === 'open' ? offer.data.entries : [];
  const autoStart =
    remembered === null && offer.data?.state === 'open' && entries.length === 1 && !start.isError;

  useEffect(() => {
    if (!autoStart || started.current) return;
    started.current = true;
    start.mutate(null);
  }, [autoStart, start.mutate]);

  const forgotten = useRef(false);
  useEffect(() => {
    if (!previous.isError || forgotten.current || remembered === null) return;
    if (!(previous.error instanceof ApiError) || previous.error.status !== 404) return;
    forgotten.current = true;
    forgetToken(secret);
  }, [previous.isError, previous.error, remembered, secret]);

  const refusal =
    start.error instanceof ApiError
      ? (sessionRefusalSentence(refusalCodeOf(start.error)) ??
        'La conversation n’a pas pu s’ouvrir. Réessayez dans un instant.')
      : start.isError
        ? 'La conversation n’a pas pu s’ouvrir. Réessayez dans un instant.'
        : null;

  if (offer.isError) return <DoorUnavailable onRetry={() => void offer.refetch()} />;
  if (offer.isPending || (remembered !== null && previous.isPending)) return <DoorLoading />;
  if (offer.data.state === 'dead') return <SessionDeadLink />;

  /*
    « Sur un lien en production, une expérience dont la page protocole porte un
    protocole refuse de démarrer tant que le moteur n'existe pas, AVEC L'ÉCRAN
    MORT » (§9.5) - and the same for a cascade that stopped resolving (§5.1).

    Only on the DIRECT start, which is the one place the refusal is about the
    whole door: a link that carries one entry and cannot open it is a link that
    can make nobody enter. On the choice page the refusal is about ONE card, and
    the dead screen would bury two experiences that still work - so it is a
    sentence there, under the heading.
  */
  if (entries.length === 1 && isDeadEntryRefusal(refusalCodeOf(start.error))) {
    return <SessionDeadLink />;
  }

  const session = previous.data;
  if (session !== undefined) {
    return (
      <SessionResumePage
        status={session.status}
        startedAt={session.startedAt}
        experienceName={session.content.name}
        onResume={() => void navigate({ to: '/s/$token', params: { token: remembered ?? '' } })}
        onRestart={() => start.mutate(session.entry.id)}
        restarting={start.isPending}
        refusal={refusal}
      />
    );
  }

  // The direct start is in flight: nothing is drawn, because there is no screen
  // between the link and the experience (§5.3).
  if (autoStart || (entries.length === 1 && start.isPending)) return <DoorLoading />;

  return (
    <SessionChoicePage
      entries={entries}
      startingId={start.isPending ? (start.variables ?? null) : null}
      onStart={(experienceId) => start.mutate(experienceId)}
      refusal={refusal}
    />
  );
}

/** The `code` a business refusal carries, out of `ApiError.details`. */
function refusalCodeOf(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return null;
  const code = (details as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

function DoorLoading() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <PmsSpinner label="Ouverture" />
    </div>
  );
}

/**
 * The link could not be READ - and that is not the same as a dead link.
 *
 * The lesson of `chat/unavailable.tsx`, and it cost an incident there: a
 * network blip rendering the dead screen tells somebody holding a perfectly
 * good invitation that it is over, and the dead screen deliberately offers no
 * way back. So a transport failure gets its own screen, and that screen carries
 * the retry.
 */
function DoorUnavailable({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <section className="mx-auto flex max-w-xl flex-col items-start gap-4 py-12">
      <h1 className="font-semibold text-2xl text-bleu-nuit">Lien momentanément injoignable</h1>
      <p className="max-w-[62ch] text-base text-gray-700">
        Votre lien est peut-être valable : la connexion n’a pas abouti. Vous pouvez réessayer sans
        quitter cette page.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="h-11 cursor-pointer rounded-md border-2 border-bleu-nuit bg-bleu-nuit px-5 font-semibold text-white"
      >
        Réessayer
      </button>
    </section>
  );
}
